import { useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { Button, Modal, Textarea, cn, useI18n } from '@xdocs/ui';
import { useAdminT } from './useAdminT.js';
import styles from './AdminConfirmDialog.module.css';

export interface AdminConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Already-localized dialog title, e.g. `Change role for "devon@studio.co"`. */
  title: string;
  /** Already-localized explanation of what this write does + what it's audited as. */
  body: string;
  /** Already-localized confirm-button verb, e.g. "Suspend account". */
  confirmLabel: string;
  destructive?: boolean;
  /** Optional extra form content rendered ABOVE the reason field (e.g. a
   * role/tier `<Select>` or a credits-delta `<Input>`) — the sensitive
   * actions that carry a structured payload alongside the reason. */
  children?: ReactNode;
  onConfirm: (reason: string) => void | Promise<void>;
  loading?: boolean;
  /** `| undefined` kept explicit (exactOptionalPropertyTypes): callers
   * routinely thread this straight through from a mutation's own
   * `error?.message` (already-optional), same convention as
   * `@xdocs/ui`'s `EmptyState.description`. */
  error?: string | undefined;
}

/**
 * Every admin sensitive-mutation dialog (F54 profile actions): confirm +
 * REQUIRED reason → audited (design/app/40-admin-user-detail.html's
 * `askConfirm`). Distinct from `@xdocs/ui`'s `ConfirmDialog` (which has no
 * reason field) — this is the admin-specific superset, kept in
 * `features/admin` rather than promoted to `@xdocs/ui` since only admin
 * mutations carry a mandatory audit reason (§24/F54 AC: "every sensitive op
 * requires a reason for the audit").
 */
export function AdminConfirmDialog({
  open,
  onOpenChange,
  title,
  body,
  confirmLabel,
  destructive = false,
  children,
  onConfirm,
  loading = false,
  error,
}: AdminConfirmDialogProps): ReactElement {
  const { t } = useI18n();
  const { t: tAdmin } = useAdminT();
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const isBusy = loading || submitting;

  function close(): void {
    setReason('');
    setReasonError(null);
    onOpenChange(false);
  }

  async function handleConfirm(): Promise<void> {
    const trimmed = reason.trim();
    if (!trimmed) {
      setReasonError(tAdmin('admin:userDetail.confirm.reasonRequired'));
      return;
    }
    setReasonError(null);
    setSubmitting(true);
    try {
      await onConfirm(trimmed);
      setReason('');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} onClose={close} title={title} size="sm">
      <p className={cn(styles['body'])}>{body}</p>
      {children}
      <Textarea
        label={tAdmin('admin:userDetail.confirm.reasonLabel')}
        required
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        {...(reasonError ? { error: reasonError } : {})}
      />
      {error && (
        <p role="alert" className={cn(styles['error'])}>
          {error}
        </p>
      )}
      <div className={cn(styles['actions'])}>
        <Button variant="secondary" onClick={close} disabled={isBusy}>
          {t('common:action.cancel')}
        </Button>
        <Button variant={destructive ? 'destructive' : 'primary'} state={isBusy ? 'loading' : 'idle'} onClick={() => void handleConfirm()}>
          {confirmLabel}
        </Button>
      </div>
    </Modal>
  );
}
